import { useCallback, useState } from 'react';
import { Download, Eye, Loader2 } from 'lucide-react';
import classNames from 'classnames';
import Button from '@/components/Button';
import {
  triggerDownload,
  triggerBulkDownload,
  isPreviewableAsset,
} from '@/services/resource-asset/resource-asset.service';
import { trackGAEvent } from '@/lib/ga-custom-events/dispatcher';

export interface ResourceAssetItem {
  documentId: string;
  title: string;
  description?: string | null;
  publishedAt?: string | null;
  updatedAt?: string | null;
  previewStatus?: 'ready' | 'processing' | 'unavailable' | null;
  scanStatus?: 'processing' | 'verified' | 'infected' | null;
  content?: {
    url: string;
    name: string;
    ext: string;
    mime: string;
    size: number;
  } | null;
  resourceAssetHistory?: Array<{
    version: number;
  }> | null;
}

export interface ResourceInfo {
  documentId: string;
  title: string;
  type?: {
    type: string;
  } | null;
  category?: {
    category: string;
  } | null;
  resourceHistory?: Array<{
    version: string | number;
  }> | null;
}

interface ResourceAssetsProps {
  assets: ResourceAssetItem[];
  resource: ResourceInfo;
  onPreview?: (asset: ResourceAssetItem | ResourceAssetItem[]) => void;
  totalSections?: number;
  sectionTitle?: string | null;
  downloadButtonText?: string;
  previewButtonText?: string;
  downloadAllButtonText?: string;
}

const getDocumentType = (mime?: string, ext?: string): string => {
  if (mime?.includes('pdf')) return 'PDF';
  if (mime?.includes('word') || ext === '.docx' || ext === '.doc') return 'Word';
  if (mime?.includes('excel') || mime?.includes('spreadsheet') || ext === '.xlsx' || ext === '.xls') return 'Excel';
  if (mime?.includes('powerpoint') || ext === '.pptx' || ext === '.ppt') return 'PowerPoint';
  if (mime?.includes('image')) return 'Image';
  if (mime?.includes('video')) return 'Video';
  if (mime?.includes('audio')) return 'Audio';
  return 'File';
};

export const ResourceAssets = (props: ResourceAssetsProps) => {
  const {
    assets,
    resource,
    onPreview,
    sectionTitle: sectionTitleProp,
    downloadButtonText = 'Download',
    previewButtonText = 'Preview',
    downloadAllButtonText = 'Download all files',
  } = props;
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());

  const getLatestResourceVersion = useCallback((): number => {
    const latestVersion = [...(resource?.resourceHistory || [])]?.sort(
      (a, b) => Number(b.version) - Number(a.version),
    )?.[0]?.version;
    return latestVersion ? Number(latestVersion) : 0;
  }, [resource?.resourceHistory]);

  const getAssetVersion = useCallback((asset: ResourceAssetItem) => {
    return asset.resourceAssetHistory?.[asset.resourceAssetHistory?.length - 1]?.version || 0;
  }, []);

  const handleDownload = useCallback(
    async (asset: ResourceAssetItem) => {
      setDownloadingIds((prev) => new Set(prev).add(asset.documentId));
      try {
        await triggerDownload(asset.documentId);
        trackGAEvent('resource_asset_downloaded', {
          resource_name: resource?.title ?? '',
          resource_type: resource?.type?.type ?? '',
          resource_id: resource?.documentId ?? '',
          resource_category: resource?.category?.category ?? '',
          resource_version: getLatestResourceVersion(),
          resource_asset_version: getAssetVersion(asset),
          resource_asset_id: asset.documentId,
        });
      } catch (error) {
        console.error('Download failed:', error);
      } finally {
        setDownloadingIds((prev) => {
          const next = new Set(prev);
          next.delete(asset.documentId);
          return next;
        });
      }
    },
    [resource, getLatestResourceVersion, getAssetVersion],
  );

  const handleDownloadAll = useCallback(async () => {
    setDownloadingIds(new Set(assets.map((asset) => asset.documentId)));
    try {
      const assetDocumentIds = assets.map((asset) => asset.documentId);
      await triggerBulkDownload(assetDocumentIds);

      const latestResourceVersion = getLatestResourceVersion();
      assets.forEach((asset) => {
        trackGAEvent('resource_asset_downloaded', {
          resource_name: resource?.title ?? '',
          resource_type: resource?.type?.type ?? '',
          resource_id: resource?.documentId ?? '',
          resource_category: resource?.category?.category ?? '',
          resource_version: latestResourceVersion,
          resource_asset_version: getAssetVersion(asset),
          resource_asset_id: asset.documentId,
        });
      });
    } catch (error) {
      console.error('Bulk download failed:', error);
    } finally {
      setDownloadingIds(new Set());
    }
  }, [assets, resource, getLatestResourceVersion, getAssetVersion]);

  const handlePreview = useCallback(
    (asset: ResourceAssetItem | ResourceAssetItem[]) => {
      if (onPreview) {
        onPreview(asset);
      }
    },
    [onPreview],
  );

  if (!assets?.length) {
    return null;
  }

  const isSingleAsset = assets.length === 1;
  const sectionTitle = sectionTitleProp ?? 'Download Toolkit';
  const isDownloadingAny = downloadingIds.size > 0;

  const renderAssetContent = (asset: ResourceAssetItem) => {
    const canPreview = isPreviewableAsset(asset.content?.mime, asset.content?.ext) && asset.previewStatus === 'ready';
    const isDownloading = downloadingIds.has(asset.documentId);

    return (
      <>
        <h4 className="mb-sh-8 font-haffer text-sh-14-20 font-bold text-black">{asset.title}</h4>
        {asset.description && (
          <p className="mb-[16px] mt-[8px] font-haffer text-sh-14-20 text-gray-600">{asset.description}</p>
        )}

        <div className="mb-[16px] flex flex-col gap-3 md:flex-row md:gap-3">
          <Button
            theme="outline"
            onClick={() => handleDownload(asset)}
            disabled={isDownloading}
            className="w-full md:w-auto">
            {downloadButtonText}
            {isDownloading ? (
              <Loader2 size={16} className="h-6 w-6 shrink-0 animate-spin" />
            ) : (
              <Download size={16} className="h-6 w-6 shrink-0" stroke="#E3786D" />
            )}
          </Button>
          {canPreview && (
            <Button
              theme="outline"
              onClick={() => handlePreview(asset)}
              disabled={isDownloading}
              className="w-full md:w-auto">
              {previewButtonText}
              <Eye size={16} className="h-6 w-6 shrink-0" stroke="#E3786D" />
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 gap-4 border-t border-people-gray-200 pt-4 md:grid-cols-4">
          <div>
            <span className="block text-sh-12-16 font-bold text-black">Document type</span>
            <span className="text-sh-12-16 font-normal text-black">
              {getDocumentType(asset.content?.mime, asset.content?.ext)}
            </span>
          </div>
          <div>
            <span className="block text-sh-12-16 font-bold text-black">Version</span>
            <span className="text-sh-12-16 font-normal text-black">
              {asset.resourceAssetHistory?.[asset.resourceAssetHistory?.length - 1]?.version || '1.0'}
            </span>
          </div>
          <div>
            <span className="block text-sh-12-16 font-bold text-black">Published on</span>
            <span className="text-sh-12-16 font-normal text-black">
              {asset.publishedAt
                ? new Date(asset.publishedAt).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  })
                : 'N/A'}
            </span>
          </div>
          <div>
            <span className="block text-sh-12-16 font-bold text-black">Updated on</span>
            <span className="text-sh-12-16 font-normal text-black">
              {asset.updatedAt
                ? new Date(asset.updatedAt).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  })
                : 'N/A'}
            </span>
          </div>
        </div>
      </>
    );
  };

  if (isSingleAsset) {
    return (
      <div className="mt-0 rounded-[8px] border border-people-gray-200 bg-light-green p-4">
        {renderAssetContent(assets[0])}
      </div>
    );
  }

  return (
    <div className="rounded-[8px] border border-people-gray-200 p-6">
      <div className="mb-sh-30">
        <h3 className="mb-sh-15 font-haffer text-sh-16-24 font-bold">{sectionTitle}</h3>
        <div className="flex flex-col gap-3 md:flex-row md:gap-3">
          <Button theme="outline" onClick={handleDownloadAll} disabled={isDownloadingAny} className="w-full md:w-auto">
            {downloadAllButtonText}
            {isDownloadingAny ? (
              <Loader2 size={16} className="h-6 w-6 shrink-0 animate-spin" />
            ) : (
              <Download size={16} className="h-6 w-6 shrink-0" stroke="#E3786D" />
            )}
          </Button>
          {assets[0].previewStatus == 'ready' && (
            <Button
              theme="outline"
              disabled={isDownloadingAny}
              onClick={() => {
                const previewableAssets = assets.filter(
                  (a) => isPreviewableAsset(a.content?.mime, a.content?.ext) && a.previewStatus === 'ready',
                );
                if (previewableAssets.length > 1) {
                  handlePreview(previewableAssets);
                } else if (previewableAssets.length === 1) {
                  handlePreview(previewableAssets[0]);
                }
              }}
              className="w-full md:w-auto">
              {previewButtonText}
              <Eye size={16} className="h-6 w-6 shrink-0" stroke="#E3786D" />
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-sh-30 gap-sh-30">
        {assets.map((asset, index) => {
          const isLast = index === assets.length - 1;

          return (
            <div
              key={asset.documentId}
              className={classNames(
                'rounded-[8px] border border-people-gray-200 bg-light-green p-4',
                isLast ? 'mb-0' : 'mb-[16px]',
              )}>
              {renderAssetContent(asset)}
            </div>
          );
        })}
      </div>
    </div>
  );
};
